/**
 * Lightweight wrapper around the browser Notification API.
 * - Permission is requested lazily (call requestPermission() right before you need it).
 * - notify() is a no-op when the tab is visible, when permission is denied, or when the API is missing.
 */

const PERMISSION_KEY = "fp_notif_permission_asked_v1";

export const isNotificationSupported = (): boolean =>
  typeof window !== "undefined" && "Notification" in window;

export const getPermission = (): NotificationPermission | "unsupported" => {
  if (!isNotificationSupported()) return "unsupported";
  return Notification.permission;
};

export const hasAskedBefore = (): boolean => {
  try {
    return localStorage.getItem(PERMISSION_KEY) === "1";
  } catch {
    return false;
  }
};

export const markAsked = () => {
  try {
    localStorage.setItem(PERMISSION_KEY, "1");
  } catch {
    // ignore storage errors
  }
};

/**
 * Requests notification permission. Returns the resulting permission state.
 * Safe to call repeatedly; the browser handles deduping.
 */
export const requestPermission = async (): Promise<NotificationPermission | "unsupported"> => {
  if (!isNotificationSupported()) return "unsupported";
  if (Notification.permission === "granted" || Notification.permission === "denied") {
    markAsked();
    return Notification.permission;
  }
  try {
    const result = await Notification.requestPermission();
    markAsked();
    return result;
  } catch {
    return "denied";
  }
};

interface NotifyOptions {
  body?: string;
  /** When true, fire even if the tab is visible. Default: false (only when hidden). */
  evenWhenVisible?: boolean;
  /** Optional click handler (focuses the window automatically before running). */
  onClick?: () => void;
  /** Optional tag to coalesce duplicates */
  tag?: string;
}

export const notify = (title: string, options: NotifyOptions = {}): void => {
  if (!isNotificationSupported()) return;
  if (Notification.permission !== "granted") return;
  if (!options.evenWhenVisible && typeof document !== "undefined" && !document.hidden) return;

  try {
    const n = new Notification(title, {
      body: options.body,
      tag: options.tag,
      icon: "/favicon.ico",
    });
    n.onclick = () => {
      try {
        window.focus();
      } catch {
        // ignore
      }
      options.onClick?.();
      n.close();
    };
  } catch {
    // ignore — some browsers throw on quota or invalid options
  }
};
